import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import PageHeader from "@/components/common/PageHeader/PageHeader";
import LearnMoreButton from "@/components/learn/LearnMoreButton";
import { Button } from "@/components/ui/button";
import { useAgentsList } from "@/hooks/agent/useAgents";
import { useActivities } from "@/hooks/analytics/useActivities";
import { useNeedsAttention, useWeeklySummary } from "@/hooks/analytics/useAnalytics";
import { useDocuments } from "@/hooks/documents/useDocumentFetch";
import { Activity, ACTIVITY_CATEGORIES, ActivityCategory } from "@/types/activity";
import ActivityFilterBar from "./components/ActivityFilterBar";
import CompactActivityRow from "./components/CompactActivityRow";
import EmptyState from "./components/EmptyState";
import NeedsAttention from "./components/NeedsAttention";
import WeeklyDigest from "./components/WeeklyDigest";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const DATE_RANGES = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "last7days" },
  { label: "Last 30 days", value: "last30days" },
  { label: "Custom range", value: "custom" },
];

const CATEGORY_OPTIONS = Object.entries(ACTIVITY_CATEGORIES).map(([, value]) => {
  let label: string;
  switch (value) {
    case "conversation":
      label = "Conversation Analysis";
      break;
    case "user_management":
      label = "User Management";
      break;
    default:
      label = value.charAt(0).toUpperCase() + value.slice(1);
  }
  return { label, value };
});

// Routine actions excluded from the audit feed by default. Reviewing your own
// edits and page visits adds zero signal — we keep them queryable via the
// "Show routine actions" toggle but hide them by default.
const ROUTINE_ACTIONS = ["viewed", "page_visited", "feature_used", "tutorial_completed"];

const ActivityPage = ({ embedded = false }: { embedded?: boolean } = {}) => {
  const { data: agents = [] } = useAgentsList();
  const { data: documents = [] } = useDocuments();
  const location = useLocation();
  const activityRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const attentionRef = useRef<HTMLDivElement | null>(null);
  const [openActivityId, setOpenActivityId] = useState<string | null>(null);

  const [dateRange, setDateRange] = useState(DATE_RANGES[2].value);
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<ActivityCategory[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showRoutineActions, setShowRoutineActions] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm]);

  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    let start: Date | null = null;
    let end: Date | null = new Date();

    switch (dateRange) {
      case "today":
        start = new Date(now.setHours(0, 0, 0, 0));
        break;
      case "yesterday":
        start = new Date(now);
        start.setDate(start.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setDate(end.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        break;
      case "last7days":
        start = new Date(now);
        start.setDate(start.getDate() - 7);
        break;
      case "last30days":
        start = new Date(now);
        start.setDate(start.getDate() - 30);
        break;
      case "custom":
        start = customStartDate;
        end = customEndDate;
        break;
    }

    return { startDate: start, endDate: end };
  }, [dateRange, customStartDate, customEndDate]);

  const { data: weeklySummary, isLoading: isLoadingSummary, isError: isSummaryError } = useWeeklySummary();
  const { data: needsAttention, isLoading: isLoadingAttention, isError: isAttentionError } = useNeedsAttention();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useActivities({
    category: selectedCategories.length > 0 ? selectedCategories : undefined,
    startDate: startDate?.toISOString(),
    endDate: endDate?.toISOString(),
    searchTerm: debouncedSearchTerm,
    excludeActions: showRoutineActions ? undefined : ROUTINE_ACTIONS,
  });

  const activities = useMemo(() => data?.pages.flatMap((page) => page.activities) ?? [], [data]);

  useEffect(() => {
    if (location.state?.openActivityId) {
      setOpenActivityId(location.state.openActivityId);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (openActivityId && activities.length > 0) {
      const activityElement = activityRefs.current[openActivityId];
      if (activityElement) {
        activityElement.scrollIntoView({ behavior: "smooth", block: "center" });

        setTimeout(() => {
          const viewDetailsButton = activityElement.querySelector("button:has(svg)");
          if (viewDetailsButton instanceof HTMLButtonElement) {
            viewDetailsButton.click();
          }
          setOpenActivityId(null);
        }, 500);
      }
    }
  }, [openActivityId, activities]);

  const scrollToAttention = () => {
    attentionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const body = (
    <>
      <WeeklyDigest
        summary={weeklySummary}
        isLoading={isLoadingSummary}
        isError={isSummaryError}
        onAttentionClick={scrollToAttention}
        onSilentClick={scrollToAttention}
      />

      <div ref={attentionRef}>
        <NeedsAttention data={needsAttention} isLoading={isLoadingAttention} isError={isAttentionError} />
      </div>

      <div className="bg-white rounded-2xl border border-gray-200">
        <div className="p-5 md:p-6 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center">
              <GenieIcon name="document" size={24} className="h-6 w-6 mr-3" />
              <div>
                <h2 className="text-lg font-bold text-[#222B45] leading-tight">Recent changes</h2>
                <p className="text-xs text-[#64748B] mt-0.5">
                  Edits to genies, knowledge, settings — and other meaningful events.
                </p>
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showRoutineActions}
                onChange={(e) => setShowRoutineActions(e.target.checked)}
                className="rounded border-gray-300 text-genie-primary focus:ring-genie-primary"
              />
              Show routine actions
            </label>
          </div>

          <ActivityFilterBar
            dateRange={dateRange}
            dateRangeOptions={DATE_RANGES}
            onDateRangeChange={setDateRange}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onCustomStartDateChange={setCustomStartDate}
            onCustomEndDateChange={setCustomEndDate}
            selectedCategories={selectedCategories}
            categoryOptions={CATEGORY_OPTIONS}
            onCategoryChange={setSelectedCategories}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </div>

        <div className="p-2 md:p-3">
          {isLoading ? (
            <div className="space-y-1.5 p-2">
              {Array(6)
                .fill(0)
                .map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2.5 animate-pulse">
                    <div className="h-9 w-9 rounded-lg bg-gray-100 flex-shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3.5 bg-gray-100 rounded w-2/3" />
                      <div className="h-3 bg-gray-100 rounded w-1/3" />
                    </div>
                  </div>
                ))}
            </div>
          ) : activities.length === 0 ? (
            <div className="py-6">
              <EmptyState
                title="No changes match your filters"
                description={
                  searchTerm || selectedCategories.length > 0
                    ? "Try clearing your filters or widening the date range."
                    : showRoutineActions
                      ? "No activity in this window."
                      : "Nothing meaningful changed in this window. Enable 'Show routine actions' to see everything."
                }
              />
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {activities.map((activity: Activity) => (
                <div
                  key={activity.id}
                  ref={(el) => {
                    activityRefs.current[activity.id] = el;
                  }}
                >
                  <CompactActivityRow activity={activity} agents={agents} documents={documents} />
                </div>
              ))}

              {isFetchingNextPage && (
                <div className="space-y-1.5 p-2">
                  {Array(3)
                    .fill(0)
                    .map((_, i) => (
                      <div key={`loading-more-${i}`} className="flex items-center gap-3 px-3 py-2.5 animate-pulse">
                        <div className="h-9 w-9 rounded-lg bg-gray-100 flex-shrink-0" />
                        <div className="flex-1 space-y-1.5">
                          <div className="h-3.5 bg-gray-100 rounded w-2/3" />
                          <div className="h-3 bg-gray-100 rounded w-1/3" />
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {!isLoading && activities.length > 0 && hasNextPage && (
            <div className="flex justify-center pt-4">
              <Button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                variant="outline"
                size="lg"
                className="min-w-[200px]"
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Load more"
                )}
              </Button>
            </div>
          )}

          {!isLoading && activities.length > 0 && !hasNextPage && (
            <div className="text-center text-xs text-gray-500 pt-4">You've reached the end of this window.</div>
          )}
        </div>
      </div>
    </>
  );

  if (embedded) {
    return <div className="overflow-x-hidden">{body}</div>;
  }

  return (
    <div className="min-h-screen bg-[#F9FAFC] overflow-x-hidden">
      <div className="container mx-auto px-4 py-8 max-w-[1920px]">
        <PageHeader
          title="Activity"
          genieIcon="activity"
          subtitle="What your genies did, what needs your attention, and the audit trail behind every change."
          action={<LearnMoreButton topic="activity" />}
        />
        {body}
      </div>
    </div>
  );
};

export default ActivityPage;
